<template>
  <div v-if="uploader.id && uploader.header" class="file-uploader">
    <div
      v-show="!edit"
      :class="{ 'file-uploader__dragging': $refs.fileUpload && $refs.fileUpload.dropActive }"
      class="file-uploader__border"
    >
      <div v-if="imagePreview" class="file-uploader__preview mb-3">
        <label class="mb-0">
          <template v-if="fileUrl">
            <img v-img :src="imageSrc" alt="" />
          </template>
          <template v-else>
            <img :src="imagePlaceholder" alt="" />
          </template>
        </label>
      </div>

      <div class="file-uploader__main">
        <file-upload
          v-if="uploader.postAction"
          v-show="!uploading"
          ref="fileUpload"
          v-model="files"
          :accept="uploader.accept"
          :data="uploader.data"
          :drop="uploader.drop"
          :headers="uploader.header"
          :input-id="uploader.id"
          :post-action="uploader.postAction"
          class="btn btn-success btn-sm"
          name="upload-file"
          @input-filter="inputFilter"
          @input-file="inputFile"
        >
          <template v-if="$refs.fileUpload && $refs.fileUpload.dropActive">
            <font-awesome-icon :icon="['fal', 'mouse-pointer']" />
            Drag & Drop Here
          </template>
          <template v-else>
            <slot>
              <font-awesome-icon :icon="['fal', 'cloud-upload']" />
              Upload
            </slot>
          </template>
        </file-upload>

        <button v-if="uploading" class="btn btn-warning btn-sm" disabled="disabled">
          Uploading {{ progress }}%...
        </button>
        <button v-if="!empty" class="btn btn-danger btn-sm ml-2" @click.prevent="clear()">
          <font-awesome-icon :icon="['fal', 'times']" />
          Remove
        </button>
      </div>
    </div>

    <div v-show="files.length && edit" class="file-uploader__edit">
      <div v-if="files.length" class="file-uploader__edit-image mb-3">
        <img ref="editImage" :src="files[0].url" alt="" class="mw-100 w-100" />
      </div>
      <div class="file-uploader__edit-buttons">
        <button class="btn btn-secondary btn-sm mr-3" type="button" @click.prevent="cancelSave">
          Cancel
        </button>
        <button class="btn btn-warning btn-sm mr-3" type="submit" @click.prevent="saveOriginal">
          Upload Original
        </button>
        <button class="btn btn-primary btn-sm" type="submit" @click.prevent="editSave">Crop</button>
      </div>
    </div>
  </div>
</template>

<script type="text/babel">
import Cropper from "cropperjs";
import FileUpload from "vue-upload-component";

export default {
  components: {
    FileUpload,
  },

  props: {
    data: {
      type: Object,
      default: () => {},
    },

    imagePlaceholder: {
      type: String,
      default:
        "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMgAAADICAAAAACIM/FCAAACVElEQVR4Ae3b1ZLjMBBA0f3/75q2s8PMzAxOTczWDoP7IZyos/e+Bk+VWdIfmZCAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQIECAAAECBAgQ0wGZGlojhqysDauFkUKCxA2rq6mRQuKhQS6BAJkcSHYx0G7GBmkFA+1vNTaIDLQZIEB+Q8L+8wJynPffog+QS9d/QID8X5A0enwuzUOqi2l5KVyLbUOSGfns1DIkCeW7fbuQakZ+FpmF3KirJ6OQGfld0ygklVo7RiFNqbVvFBJJrT2jkJbUOjQKKUP53a1RiNusvS13tZqFDUjaZhep9h9tQNyF/GimdLVimalsQNzRD0fm6h0HcmcE4qLpj7fsla5eNScyVxmBvFB2drcOb3Oni+WlOwMQld6yROZK85BqTl67Mw+J9cW9GYjesl56sgjRW5beS6xBYvkssgjRW5YE6xYhessSCXPLkFi+iyxDToLvj69bhOgtS6SRW4UkJ3Pys8gkJH5RBPKrdXuQRCteahQWIFqhiyxAtEK3bgVSXiiF3rYMQMoVpaj1ZALS3hGsW4BoR5ttyyeIdrQp8h7SmSNY9xSiHW0KW55CtKNNjdhniHa0l/gI0Y72Eh8h2tFe4g3kpMg/KjLlaCt5LryZQRf+SLovfI1ZpkC8hjA3Pj0baFcV60eAAPEOkk7IOkRZ3xpWS8Ja3YkICBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAAQIECBAgQIAAsd8/ZCwRPHF77LkAAAAASUVORK5CYII=",
    },

    imagePreview: {
      type: Boolean,
      default: false,
    },

    fileUrl: {
      type: String,
      default: "",
    },

    aspectRatio: {
      default: NaN,
    },

    postAction: {
      default: null,
    },

    drag: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      files: [],
      edit: false,
      cropper: false,
      uploading: false,
      progress: 0,

      uploader: {
        id: null,
        header: null,
        accept: "image/png,image/gif,image/jpeg,image/webp,text/plain,application/pdf",
        data: {
          key: "upload-file",
        },
        postAction: null,
        drop: true,
      },
    };
  },

  computed: {
    imageSrc() {
      return this.files.length ? this.files[0].url : this.fileUrl;
    },

    empty() {
      return this.fileUrl === "" || this.fileUrl === null;
    },
  },

  watch: {
    data() {
      this.uploader.data = { ...this.uploader.data, ...this.data };
    },

    edit(value) {
      if (value) {
        this.$nextTick(() => {
          if (!this.$refs.editImage) {
            return;
          }

          this.cropper = new Cropper(this.$refs.editImage, {
            aspectRatio: this.aspectRatio,
            viewMode: 1,
          });
        });
      } else if (this.cropper) {
        this.cropper.destroy();
        this.cropper = false;
      }
    },
  },

  mounted() {
    this.initUploader();
  },

  methods: {
    initUploader() {
      this.uploader.header = {
        Authorization: `Bearer ${this.$auth.token()}`,
      };

      this.uploader.id = `file-uploader-${window.generateUUID()}`;
      this.uploader.drag = this.drag;

      if (this.postAction === null) {
        this.uploader.postAction = `${process.env.VUE_APP_API}/businesses/upload`;
      } else {
        this.uploader.postAction = `${process.env.VUE_APP_API}/${this.postAction}`;
      }

      this.uploader.data = { ...this.uploader.data, ...this.data };
    },

    clear() {
      this.$refs.fileUpload.clear();
      // eslint-disable-next-line vue/no-mutating-props
      this.fileUrl = "";
      this.$emit("notEditing");
      this.$emit("uploaded", "");
    },

    cancelSave() {
      this.$refs.fileUpload.clear();
      this.$emit("notEditing");
    },

    saveOriginal() {
      this.edit = false;
      const oldFile = this.files[0];
      const file = oldFile.file;

      this.$refs.fileUpload.update(oldFile.id, {
        file,
        type: file.type,
        size: file.size,
        active: true,
      });

      this.$emit("notEditing");
      this.uploading = true;
    },

    editSave() {
      if (!this.cropper) {
        return;
      }

      this.edit = false;
      const oldFile = this.files[0];
      const binStr = atob(this.cropper.getCroppedCanvas().toDataURL(oldFile.type).split(",")[1]);
      const arr = new Uint8Array(binStr.length);
      for (let i = 0; i < binStr.length; i += 1) {
        arr[i] = binStr.charCodeAt(i);
      }
      const file = new File([arr], oldFile.name, { type: oldFile.type });
      this.$refs.fileUpload.update(oldFile.id, {
        file,
        type: file.type,
        size: file.size,
        active: true,
      });

      this.$emit("notEditing");
      this.uploading = true;
    },

    upload() {
      if (!this.$refs.fileUpload || !this.$refs.fileUpload.active) {
        this.$refs.fileUpload.active = true;
        this.uploading = true;
      }
    },

    inputFile(newFile, oldFile) {
      this.edit = false;

      if (newFile && !oldFile) {
        // Image or other allow file types
        if (/\.(gif|jpg|jpeg|png|webp)$/i.test(newFile.name)) {
          this.$nextTick(() => {
            this.$emit("editing");
            this.edit = true;
          });
        } else {
          this.$nextTick(() => {
            this.upload();
          });
        }
      }

      if (newFile && oldFile && newFile.success && !oldFile.success) {
        if (this.$lodash.has(newFile, "response.url")) {
          this.$emit("uploaded", newFile.response.url);
        } else {
          this.$emit("uploaded", newFile.response);
        }

        this.uploading = false;
        this.progress = 0;
      }

      if (newFile && oldFile && newFile.progress !== oldFile.progress) {
        this.progress = newFile.progress;
      }
    },

    inputFilter(newFile, oldFile) {
      /* eslint-disable no-param-reassign */
      if (newFile && (!oldFile || newFile.file !== oldFile.file)) {
        newFile.url = "";
        const URL = window.URL || window.webkitURL;
        if (URL && URL.createObjectURL) {
          newFile.url = URL.createObjectURL(newFile.file);
        }
      }

      return true;
    },
  },
};
</script>

<style lang="css" rel="stylesheet/css" scoped>
.file-uploader__border {
  border: 1px dashed #ffffff;
}

.file-uploader__dragging {
  border: 1px dashed #c8ced3;
}
</style>
